import React, { useContext } from 'react';
import { ThemeContext } from 'grommet';
import { normalizeColor } from 'grommet/utils';
import { Area, AreaChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import ChartTooltip from './ChartTooltip';

// Days to pay by month, against the terms. `points` come from paymentPeriodPoints or trendPoints.
const PaymentLine = ({ points, netTerms, height = 190, id = 'payment-period-wash' }) => {
    const theme = useContext(ThemeContext);
    const color = (name) => normalizeColor(name, theme);
    const primary = color('barchart-primary');
    const surface = color('background');
    const lastIndex = points.reduce((last, point, index) => (point.number_of_days != null ? index : last), -1);
    // Above the slowest month and the terms line, so neither sits on the top edge
    const top = Math.max(netTerms || 0, ...points.map((point) => point.number_of_days || 0), 1) * 1.15;

    const renderTooltip = ({ active, payload }) => {
        const point = payload?.[0]?.payload;
        if (!active || point?.number_of_days == null) return null;
        return <ChartTooltip title={point.month} lines={[`${Math.round(point.number_of_days)} days`]} />;
    };

    return (
        <ResponsiveContainer width="100%" height={height}>
            {/* Room on the right for the terms label, outside the plot where the line can't cover it */}
            <AreaChart data={points} margin={{ top: 8, right: netTerms > 0 ? 56 : 16, left: 16, bottom: 0 }}>
                <defs>
                    <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={primary} stopOpacity={.14} />
                        <stop offset="100%" stopColor={primary} stopOpacity={0} />
                    </linearGradient>
                </defs>
                <XAxis
                    dataKey="display_month_string"
                    interval={0}
                    axisLine={false}
                    tickLine={false}
                    tick={{ fontSize: 16, fill: color('text-weak') }}
                />
                <YAxis hide domain={[0, top]} />
                {netTerms > 0 && (
                    <ReferenceLine
                        y={netTerms}
                        stroke={color('text-weak')}
                        strokeDasharray="4 4"
                        label={{ value: `Net ${netTerms}`, position: 'right', fill: color('text-weak'), fontSize: 14 }}
                    />
                )}
                <Tooltip isAnimationActive={false} cursor={{ stroke: color('text-weak') }} content={renderTooltip} />
                <Area
                    type="monotone"
                    dataKey="number_of_days"
                    connectNulls
                    stroke={primary}
                    strokeWidth={2}
                    fill={`url(#${id})`}
                    isAnimationActive={false}
                    dot={({ cx, cy, index }) => (index === lastIndex
                        ? <circle key="latest" cx={cx} cy={cy} r={5} fill={primary} stroke={surface} strokeWidth={2} />
                        : <g key={index} />)}
                    activeDot={{ r: 5, fill: primary, stroke: surface, strokeWidth: 2 }}
                />
            </AreaChart>
        </ResponsiveContainer>
    );
};

export default PaymentLine;
